import React from 'react'
import { Control, Controller, Path, UseFormSetValue } from 'react-hook-form'
import { IDropdownOption } from '../../../interfaces/IDropdownOptions'
import { Dropdown, makeStyles, Option } from '@fluentui/react-components'
import { t } from 'i18next'
import { Field } from './Field'
import { Dismiss20Regular } from '@fluentui/react-icons'
import { ConditionalTooltip } from '../other/ConditionalTooltip'

const useStyles = makeStyles({
   dismissIcon: {
      padding: `9px 0`,
      marginRight: '4px',
   },
})

type ControlledDropdownProps<T> = {
   name: keyof T
   control: Control<T, any>
   label: string | JSX.Element
   customLabel?: JSX.Element
   options: IDropdownOption[]
   showTooltip?: boolean
   tooltipMsg?: string
   disabled?: boolean
   required?: boolean
   clearable?: boolean
   deselectable?: boolean
}
export const ControlledDropDown = <T extends {}>({
   name,
   control,
   label,
   options,
   disabled,
   showTooltip,
   tooltipMsg,
   required,
   clearable,
   deselectable,
}: ControlledDropdownProps<T>) => {
   const classes = useStyles()
   return (
      <Controller
         name={name as any}
         control={control}
         rules={{ required: required ? t('RequiredField') : false }}
         render={({ field: { value, onChange }, fieldState: { error }, formState: { disabled: formDisabled } }) => (
            <Field label={label} required={required ?? false} validationMessage={error ? error.message : null} validationState={error ? 'error' : null}>
               <ConditionalTooltip show={showTooltip} content={tooltipMsg}>
                  <Dropdown
                     clearable={clearable}
                     clearButton={clearable ? <Dismiss20Regular className={classes.dismissIcon} /> : undefined}
                     value={value || value === 0 ? options.find((x) => x.key.toString() === value.toString())?.text : ''}
                     onOptionSelect={(e, d) => {
                        const rawValue = d.optionValue
                        if (rawValue === undefined || rawValue === '') {
                           onChange(null)
                           return
                        }
                        if (deselectable && (value || value === 0) && rawValue === value.toString()) {
                           onChange(null)
                           return
                        }
                        const isNumeric = rawValue !== '' && !isNaN(Number(rawValue)) && !rawValue.includes('-')
                        onChange(isNumeric ? Number(rawValue) : rawValue)
                     }}
                     selectedOptions={[value || value === 0 ? value.toString() : '']}
                     disabled={(disabled || formDisabled) ?? false}
                     style={{ minWidth: 'auto' }}
                  >
                     {options.map((x) => (
                        <Option key={x.key} value={x.key.toString()} disabled={x.disabled ? x.disabled : false}>
                           {x.text}
                        </Option>
                     ))}
                  </Dropdown>
               </ConditionalTooltip>
            </Field>
         )}
      />
   )
}

type ControlledDropdownClearableProps<T> = {
   name: Path<T>
   control: Control<T, any>
   items: any[]
   label: string | JSX.Element
   customLabel?: JSX.Element
   disabled?: boolean
   required?: boolean
   setValue: UseFormSetValue<any>
   setValueString: string
   showTooltip?: boolean
   tooltipMsg?: string
}
export const ControlledDropDownClearable = <T extends {}>({
   name,
   items,
   control,
   label,
   disabled,
   required,
   setValue,
   setValueString,
   showTooltip,
   tooltipMsg,
}: ControlledDropdownClearableProps<T>) => {
   const classes = useStyles()
   return (
      <Controller
         name={name}
         control={control}
         render={({ field: { value, onChange } }) => {
            const typedValue = value as { id: string; name: string } | null
            const selectedId = typedValue?.id ?? null
            const selectedName = typedValue?.name ?? ''
            return (
               <Field label={label} required={required ?? false}>
                  <ConditionalTooltip show={showTooltip} content={tooltipMsg}>
                     <Dropdown
                        clearable
                        clearButton={<Dismiss20Regular className={classes.dismissIcon} />}
                        disabled={disabled}
                        selectedOptions={selectedId ? [selectedId] : []}
                        value={selectedId ? selectedName : ''}
                        onOptionSelect={(_, data) => {
                           const picked = items.find((i) => i.id === data.optionValue) ?? null
                           onChange(picked)
                           setValue(setValueString, picked?.id ?? null)
                        }}
                     >
                        {items.map((p) => (
                           <Option key={p.id} value={p.id}>
                              {p.name}
                           </Option>
                        ))}
                     </Dropdown>
                  </ConditionalTooltip>
               </Field>
            )
         }}
      />
   )
}
